import { useEffect } from 'react';
import flatpickr from 'flatpickr';
import 'flatpickr/dist/flatpickr.css';
import Label from './Label';
import { CalenderIcon } from '../../icons';
import { twMerge } from 'tailwind-merge';

const FP_TAB_SENTINEL = '__FP_TAB_NONE__';

/** While the calendar is closed, flatpickr still leaves buttons in the tab order; strip them so Tab goes to the next real field. */
function stripFlatpickrCalendarTabStops(instance) {
  const cal = instance?.calendarContainer;
  if (!cal) return;
  cal.querySelectorAll("button, select, input, textarea, a[href]").forEach((el) => {
    if (el.dataset.fpOrigTabindex !== undefined) {
      el.setAttribute("tabindex", "-1");
      return;
    }
    const cur = el.getAttribute("tabindex");
    el.dataset.fpOrigTabindex = cur === null ? FP_TAB_SENTINEL : cur;
    el.setAttribute("tabindex", "-1");
  });
}

function restoreFlatpickrCalendarTabStops(instance) {
  const cal = instance?.calendarContainer;
  if (!cal) return;
  cal.querySelectorAll("[data-fp-orig-tabindex]").forEach((el) => {
    const v = el.dataset.fpOrigTabindex;
    if (v === FP_TAB_SENTINEL || v === "") el.removeAttribute("tabindex");
    else el.setAttribute("tabindex", v);
  });
}

export default function DatePicker({
  id,
  mode,
  onChange,
  label,
  defaultDate,
  placeholder,
  disabled = false,
  /** Extra classes for the date input (e.g. compact height). */
  inputClassName = "",
}) {
  useEffect(() => {
    const flatPickr = flatpickr(`#${id}`, {
      mode: mode || "single",
      /** Inline/static calendars sit in the layout stack; sibling rows (and overflow parents) draw on top — append to body so the popup layers correctly. */
      static: false,
      appendTo: typeof document !== "undefined" ? document.body : undefined,
      monthSelectorType: "static",
      dateFormat: "Y-m-d",
      defaultDate,
      allowInput: true,
      onChange,
      onReady(_d, _s, instance) {
        requestAnimationFrame(() => stripFlatpickrCalendarTabStops(instance));
      },
      onOpen(_d, _s, instance) {
        restoreFlatpickrCalendarTabStops(instance);
      },
      onClose(_d, _s, instance) {
        stripFlatpickrCalendarTabStops(instance);
      },
    });

    return () => {
      if (!Array.isArray(flatPickr)) {
        flatPickr.destroy();
      }
    };
  }, [mode, onChange, id, defaultDate]);

  return (
    <div>
      {label && <Label htmlFor={id}>{label}</Label>}

      <div className="relative">
        <input
          id={id}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete="off"
          className={twMerge(
            "h-11 w-full rounded-lg border appearance-none px-4 py-2.5 text-sm shadow-theme-xs placeholder:text-gray-400 focus:outline-hidden focus:ring-3 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30 bg-transparent text-gray-800 border-gray-300 focus:border-brand-300 focus:ring-brand-500/20 dark:border-gray-700 dark:focus:border-brand-800",
            disabled ? "opacity-60 cursor-not-allowed" : "",
            inputClassName
          )}
        />

        <span className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400">
          <CalenderIcon className="size-5 shrink-0 opacity-80" />
        </span>
      </div>
    </div>
  );
}
